import { Ref, ref, shallowRef, watch } from "vue";

import type {
  Controller,
  GeneralizedTaskId,
  GraphDatabase,
  GraphRecordResult,
  GraphResult,
  GraphResultGraphErrorResult,
  ReleasableRef,
  TaskBinding,
  TaskBindingStatusLegacy,
  TaskBindingStatusMapping,
  TaskBindingStatusValues,
  WorkspaceId,
  WorkspaceMembers,
} from "@ctrl/controller-rpc";

import { createObject, getDateFrom } from "@/components/graph/component";
import {
  asGraphDateRecord,
  asGraphRecordResult,
  asString,
} from "@/components/graph/result";
import { extractTitleFromPlainRecord } from "@/components/graph/title";
import { memberRelationFieldName } from "@/components/graph/utils";
import type { UseProfile } from "@/composables/useProfile";
import { withRelease } from "@/ref";
import { memberEmail } from "@/utils/members";

/** The status of an organization task, as mapped by its binding. */
export type OrgTaskStatus = "todo" | "done" | "discarded";

/** One task of an organization workspace, with the row data the org Tasks
 * screen groups on. */
export type OrgTask = {
  id: GeneralizedTaskId;
  title?: string;
  status: OrgTaskStatus;
  /** Row id of the assigned member, when the type has a member relation. */
  assigneeId?: string;
  assigneeName?: string;
  /** Email of the assigned member (avatar resolution). */
  assigneeEmail?: string;
  /** Raw member relation cell, as displayed by the assignee picker. */
  assignee?: GraphRecordResult;
  /** The task's scheduled date, when the binding maps one. */
  scheduled?: Date;
  /** The bound table the row belongs to. */
  database: GraphDatabase;
  /** Raw row id within the table. */
  rowId: string;
};

/** One Assigned-tab section, including members who currently have no tasks. */
export type AssignedTaskGroup = {
  memberId: string;
  name: string;
  email?: string;
  assignee?: GraphRecordResult;
  taskIds: GeneralizedTaskId[];
};

/**
 * Group uncompleted assigned tasks by workspace member. The member directory
 * seeds the groups so every member gets an Assigned-tab section; task data is
 * then merged in and remains as a fallback for stale assignments.
 */
export const groupAssignedTasksByMember = (
  tasks: OrgTask[],
  members: WorkspaceMembers
): AssignedTaskGroup[] => {
  const groups = new Map<string, AssignedTaskGroup>();
  for (const member of Object.values(members)) {
    groups.set(member.id, {
      memberId: member.id,
      name: member.name || "Unknown member",
      email: member.email,
      assignee: {
        Title: member.name,
        Email: member.email,
        _routine: { id: member.id },
      },
      taskIds: [],
    });
  }

  for (const task of tasks) {
    if (task.status !== "todo" || !task.assigneeId) continue;
    const group = groups.get(task.assigneeId) ?? {
      memberId: task.assigneeId,
      name: task.assigneeName ?? "Unknown member",
      email: task.assigneeEmail,
      assignee: task.assignee,
      taskIds: [],
    };
    group.taskIds.push(task.id);
    if (task.assignee) group.assignee = task.assignee;
    groups.set(task.assigneeId, group);
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

/** One workspace section of the org Tasks screen. */
export type OrgWorkspaceTasks = {
  workspaceId: WorkspaceId;
  workspaceName: string;
  /** Whether this workspace's bindings and rows are still being resolved. */
  loading: boolean;
  /**
   * Live list of every task of the workspace's bound tables. Unlike the task
   * list RPCs, this is not restricted by the bindings' filters (e.g.
   * "assigned to me"): the org screen shows all the organization's tasks.
   * `undefined` until the first rows query has been mapped to task ids.
   */
  tasks: Ref<OrgTask[] | undefined>;
  /**
   * Create a task (a row of the workspace's bound tasks table), optionally
   * assigned to a member (a raw member relation cell, e.g. another task's
   * `assignee`). Only exposed on workspaces with a task binding: creation
   * never provisions anything. Resolves with the created task's id (so the
   * caller can e.g. select it), or undefined when nothing was created.
   */
  addTask?: (
    title: string,
    assignee?: GraphRecordResult
  ) => Promise<GeneralizedTaskId | undefined>;
};

/**
 * The comparable value of a status mapping cell: select mappings encode
 * values as cells (`{ type, value }`), booleans and plain strings as-is.
 */
const legacyStatusValue = (
  value: TaskBindingStatusLegacy | undefined
): unknown => {
  if (
    value !== null &&
    value !== undefined &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Map) &&
    "value" in value
  )
    return value.value;
  return value;
};

/**
 * The value written by a binding's status mapping for the todo state, as a
 * graph value (select mappings write string cells, `{ type, value }`).
 */
const statusWriteValue = (binding: TaskBinding): string | undefined => {
  const write = legacyStatusValue(binding.status_mapping?.todo.write);
  return typeof write === "string" ? write : undefined;
};

/** The task status a row's status cell maps to (todo when unmapped). */
const statusOfCell = (
  mapping: TaskBindingStatusMapping | undefined,
  cell: GraphResult | undefined
): OrgTaskStatus => {
  if (!mapping || cell === undefined || cell === null) return "todo";
  const matches = (values: TaskBindingStatusValues): boolean => {
    const reads = values.read ?? [values.write];
    return reads.some((read) => legacyStatusValue(read) === cell);
  };
  if (matches(mapping.done)) return "done";
  if (matches(mapping.discarded)) return "discarded";
  return "todo";
};

/**
 * The date of a graph date cell. Query results deliver dates as records
 * (`{ Year, Month, Day }`, sometimes lowercased, numbers possibly bigint).
 */
const dateOfCell = (cell: GraphResult | undefined): Date | undefined => {
  const date = asGraphDateRecord(cell);
  return date ? getDateFrom(date) : undefined;
};

/** The rows of a `databases.[...].rows` query result, in result order. */
const rowsOfResult = (
  result: GraphResult | null | undefined
): Array<{ rowId: string; record: GraphRecordResult }> => {
  if (!(result instanceof Map)) return [];
  const rows: Array<{ rowId: string; record: GraphRecordResult }> = [];
  for (const [key, raw] of result.entries()) {
    const record = asGraphRecordResult(raw);
    if (!record) continue;
    const routine = asGraphRecordResult(record._routine);
    const rowId = asString(key) ?? (routine ? asString(routine.id) : undefined);
    if (rowId) rows.push({ rowId, record });
  }
  return rows;
};

/** A bound table of a workspace, with its live rows query. */
type BoundTable = {
  binding: TaskBinding;
  database: GraphDatabase;
  rowsRef: ReleasableRef<GraphResultGraphErrorResult>;
  /** Field names of the mapped columns, resolved from the table schema. */
  statusField?: string;
  scheduledField?: string;
  assigneeField?: string;
};

const queryBoundTable = async (
  controller: Controller,
  binding: TaskBinding
): Promise<BoundTable | undefined> => {
  const database = (
    await controller.graph.database.get(binding.workspace_id, binding.table_id)
  ).release();
  if (!database) return undefined;

  // Live query of all the table's rows, deliberately ignoring the binding's
  // filter (reactive: the ref updates when rows are added or removed).
  const compiledRef = await controller.graph.query.compile(
    binding.workspace_id,
    `databases.["${binding.table_id}"].rows`,
    {}
  );
  const compiled = compiledRef.value;
  compiledRef.release();
  if (!compiled || "error" in compiled) {
    window.console.error("Unable to compile the tasks query:", compiled);
    return undefined;
  }
  const rowsRef = await controller.graph.query.run(compiled.value);

  const fieldName = (columnId: string | undefined): string | undefined =>
    columnId ? database.type.fields[columnId]?.name : undefined;
  return {
    binding,
    database,
    rowsRef,
    statusField: fieldName(binding.status_mapping?.column),
    scheduledField: fieldName(binding.scheduled_mapping),
    assigneeField: memberRelationFieldName(database),
  };
};

const queryBoundTableBestEffort = async (
  controller: Controller,
  binding: TaskBinding
): Promise<BoundTable | undefined> => {
  try {
    return await queryBoundTable(controller, binding);
  } catch (error) {
    window.console.error("Unable to query a bound tasks table:", error);
    return undefined;
  }
};

/** The task attributes of a row, derived from its cells and the binding. */
const taskOfRow = (
  table: BoundTable,
  rowId: string,
  record: GraphRecordResult
): Omit<OrgTask, "id"> => {
  const statusCell = table.statusField ? record[table.statusField] : undefined;
  const status = statusOfCell(table.binding.status_mapping, statusCell);

  const assignee = table.assigneeField
    ? asGraphRecordResult(record[table.assigneeField])
    : undefined;
  const assigneeRoutine = assignee
    ? asGraphRecordResult(assignee._routine)
    : undefined;
  const assigneeId = assigneeRoutine ? asString(assigneeRoutine.id) : undefined;

  // Scheduling lives in the mapped column when there is one, otherwise in
  // the binding's per-row data (snapshot: bindings are fetched once).
  const mappedDate = table.scheduledField
    ? dateOfCell(record[table.scheduledField])
    : undefined;
  const rowScheduling = table.binding.rows?.[rowId]?.scheduling;
  const scheduled =
    mappedDate ?? (rowScheduling instanceof Date ? rowScheduling : undefined);

  return {
    title: extractTitleFromPlainRecord(record),
    status,
    assigneeId,
    assigneeName:
      assignee && assigneeId
        ? extractTitleFromPlainRecord(assignee)
        : undefined,
    assigneeEmail: assignee && assigneeId ? memberEmail(assignee) : undefined,
    assignee: assignee && assigneeId ? assignee : undefined,
    scheduled,
    database: table.database,
    rowId,
  };
};

/**
 * Load the live per-workspace task lists for an organization's Tasks screen.
 *
 * For each of the org's workspaces with task bindings, queries all the rows
 * of the bound tables and exposes them as tasks (id + status, assignee and
 * scheduled date derived from the row), along with an add-task function
 * creating a row in the workspace's tasks table.
 *
 * The returned ref owns the live queries.
 */
const loadOrgWorkspaceTasks = async (
  controller: Controller,
  workspaces: Array<{ id: WorkspaceId; name: string }>
): Promise<Readonly<ReleasableRef<OrgWorkspaceTasks[]>>> => {
  const bindingsRef = await controller.task.binding.byWorkspace();
  const allBindings = Object.values(bindingsRef.value ?? {}).flatMap(
    (dictionary) => Object.values(dictionary)
  );
  bindingsRef.release();

  const sections = (
    await Promise.all(
      workspaces.map(async (workspace) => {
        // The bound tables of the workspace (a table may have several
        // bindings, e.g. differently filtered ones: query it only once).
        const bindingByTable = new Map<string, TaskBinding>();
        for (const binding of allBindings) {
          if (binding.workspace_id !== workspace.id) continue;
          if (!bindingByTable.has(binding.table_id))
            bindingByTable.set(binding.table_id, binding);
        }
        if (bindingByTable.size === 0) return [];

        const tables = (
          await Promise.all(
            Array.from(bindingByTable.values()).map((binding) =>
              queryBoundTableBestEffort(controller, binding)
            )
          )
        ).flatMap((table) => (table ? [table] : []));
        if (tables.length === 0) return [];

        const resource = await workspaceSection(
          controller,
          workspace.id,
          workspace.name,
          tables
        );
        return [{ ...resource, tables }];
      })
    )
  ).flat();

  return withRelease(
    // shallowRef: the sections list is static (only the inner `tasks` refs
    // are live), and `ref` would unwrap them in the type.
    shallowRef(sections.map(({ section }) => section)),
    () =>
      void sections.forEach(({ tables, release }) => {
        release();
        tables.forEach(({ rowsRef }) => rowsRef.release());
      })
  );
};

const workspaceSection = async (
  controller: Controller,
  workspaceId: WorkspaceId,
  workspaceName: string,
  tables: BoundTable[]
): Promise<{ section: OrgWorkspaceTasks; release: () => void }> => {
  // Promote raw row ids to generalized task ids (stringified object ids).
  // Memoized: the same rows come back on every update.
  const objectIdCache = new Map<string, Promise<GeneralizedTaskId>>();
  const objectIdOf = (
    table: BoundTable,
    rowId: string
  ): Promise<GeneralizedTaskId> => {
    const key = `${table.binding.table_id}/${rowId}`;
    const cached = objectIdCache.get(key);
    if (cached) return cached;
    const promise = controller.graph.object.id.make({
      workspace_id: workspaceId,
      table_id: table.binding.table_id,
      row_id: rowId,
    });
    objectIdCache.set(key, promise);
    return promise;
  };

  const tasks = ref<OrgTask[] | undefined>(undefined);
  let generation = 0;
  const stop = watch(
    tables.map(({ rowsRef }) => rowsRef),
    async () => {
      const currentGeneration = ++generation;
      const next = await Promise.all(
        tables.flatMap((table) => {
          const result = table.rowsRef.value;
          const rows =
            result && !("error" in result) ? result.value : undefined;
          return rowsOfResult(rows).map(async ({ rowId, record }) => ({
            id: await objectIdOf(table, rowId),
            ...taskOfRow(table, rowId, record),
          }));
        })
      );
      // A newer result superseded this computation.
      if (currentGeneration !== generation) return;
      tasks.value = next;
    },
    { immediate: true, deep: true }
  );

  // Add-task target: prefer the auto-provisioned Task type, fall back to the
  // first bound table alphabetically.
  const sorted = [...tables].sort(
    (a, b) =>
      Number(b.database.data_name === "Task") -
        Number(a.database.data_name === "Task") ||
      a.database.data_name.localeCompare(b.database.data_name)
  );
  const addTarget = sorted[0];

  const addTask = async (
    title: string,
    assignee?: GraphRecordResult
  ): Promise<GeneralizedTaskId | undefined> => {
    if (!addTarget) return undefined;
    const { database, binding, assigneeField } = addTarget;
    const properties: Record<string, GraphResult> = {};

    // Set the status column to the binding's todo value so the new row starts
    // in the todo state (an unset status may not be mapped as a task).
    const statusColumnId = binding.status_mapping?.column;
    const statusColumnName = statusColumnId
      ? database.type.fields[statusColumnId]?.name
      : undefined;
    const todoValue = statusWriteValue(binding);
    if (statusColumnName && todoValue !== undefined)
      properties[statusColumnName] = todoValue;

    // Assign on creation when a member is given (e.g. the Assigned tab's
    // per-member sections); otherwise the assignee is deliberately left
    // empty: assigning is a separate action.
    if (assignee && assigneeField) {
      properties[assigneeField] = assignee;
    }

    const rowId = await createObject(
      database,
      controller,
      title,
      Object.keys(properties).length > 0 ? properties : undefined
    );
    // The generalized task id the live list will use for this row, so the
    // caller can select the task as soon as it appears.
    return rowId ? objectIdOf(addTarget, rowId) : undefined;
  };

  return {
    section: {
      workspaceId,
      workspaceName,
      loading: false,
      tasks,
      addTask,
    },
    release: () => {
      generation += 1;
      stop();
    },
  };
};

const emptyWorkspaceSection = (
  workspaceId: WorkspaceId,
  workspaceName: string,
  loading: boolean
): OrgWorkspaceTasks => ({
  workspaceId,
  workspaceName,
  loading,
  tasks: ref([]),
});

/**
 * Live task data for every workspace of an organization.
 *
 * The sections ref is returned synchronously with loading workspace
 * sections so the route never suspends on controller reads. Existing
 * bindings hydrate in the background. A workspace with no binding exposes
 * no `addTask` — creation never provisions, and the screen-level inspection
 * (`useProvisionedRepair`) is responsible for surfacing broken workspaces.
 * `reload` re-reads the bindings and rows, e.g. after a repair.
 */
export const useTeamTasks = (
  controller: Controller,
  profile: Ref<UseProfile>,
  orgId: string
): {
  sections: Readonly<ReleasableRef<OrgWorkspaceTasks[]>>;
  reload: () => Promise<void>;
} => {
  const workspaces =
    profile.value.organizations.find((org) => org.id === orgId)?.workspaces ??
    [];
  const sections = shallowRef<OrgWorkspaceTasks[]>([]);

  let current: Readonly<ReleasableRef<OrgWorkspaceTasks[]>> | undefined;
  let generation = 0;
  let released = false;
  let loadingSections: OrgWorkspaceTasks[] = [];
  let settledEmptySections: OrgWorkspaceTasks[] = [];

  const load = async (settled = false): Promise<void> => {
    const loadGeneration = ++generation;
    const loaded = await loadOrgWorkspaceTasks(controller, workspaces);
    if (released || loadGeneration !== generation) {
      loaded.release();
      return;
    }

    current?.release();
    current = loaded;
    const byWorkspace = new Map(
      loaded.value.map((workspace) => [workspace.workspaceId, workspace])
    );
    const fallbackSections = settled ? settledEmptySections : loadingSections;
    sections.value = fallbackSections.map(
      (fallback) => byWorkspace.get(fallback.workspaceId) ?? fallback
    );
  };

  const logLoadError = (error: unknown): void => {
    if (!released) {
      sections.value = settledEmptySections;
      window.console.error("Unable to load organization tasks:", error);
    }
  };

  const emptySections = (loading: boolean): OrgWorkspaceTasks[] =>
    workspaces.map((workspace) =>
      emptyWorkspaceSection(workspace.id, workspace.name, loading)
    );
  loadingSections = emptySections(true);
  settledEmptySections = emptySections(false);
  sections.value = loadingSections;

  // The initial read never provisions: an unconfigured workspace becomes an
  // empty task list immediately after the bindings lookup.
  void load(true).catch(logLoadError);

  return {
    sections: withRelease(sections, () => {
      released = true;
      generation += 1;
      current?.release();
      current = undefined;
    }),
    reload: () => load(true),
  };
};
